import React, { useState } from 'react';
import { LogIn, X, Loader2, User } from 'lucide-react';
import api from '../services/api';
import LanguageSelector from './LanguageSelector';

const BRAND_BLUE = '#2B4B6A';
const TRADOS_LOGO = 'https://www.trados.co/wp-content/uploads/2026/03/Trados-Trade-Verified.png';

/**
 * Shared public header bar used across all public-facing pages.
 * Yellow background, Trados pill logo, nav links, and Login button with modal.
 *
 * Props:
 *   - loggedInUser: user object if logged in (shows Dashboard/Logout instead of Login button)
 *   - onLogout: callback when user clicks Logout
 *   - sticky: whether the header is sticky (default true)
 *   - maxWidth: max-width class for inner container (default "max-w-5xl")
 */
const TradosPublicHeader = ({ loggedInUser = null, onLogout, sticky = true, maxWidth = 'max-w-5xl' }) => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) return;
    setLoggingIn(true);
    setLoginError('');
    try {
      const res = await api.post('/auth/login', { email: loginEmail, password: loginPassword });
      if (res.data?.access_token) {
        localStorage.setItem('token', res.data.access_token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        window.location.reload();
      }
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoggingIn(false);
    }
  };

  return (
    <>
      <div
        className={`border-b shadow-sm ${sticky ? 'sticky top-0 z-20' : ''}`}
        style={{ backgroundColor: '#FFFDE7' }}
      >
        <div className={`${maxWidth} mx-auto px-4 py-3 flex items-center justify-between`}>
          {/* Logo */}
          <a href="https://www.trados.app" className="flex items-center">
            <img src={TRADOS_LOGO} alt="Trados" className="h-16" />
          </a>

          {/* Nav + Auth */}
          <div className="flex items-center gap-4">
            <nav className="hidden sm:flex items-center gap-1 text-sm font-medium text-[#2B4B6A]">
              <a href="https://www.trados.app" className="px-2 py-1 hover:text-[#3a6590] transition-colors">Home</a>
              <span className="text-gray-300">|</span>
              <a href="/create-loi" className="px-2 py-1 hover:text-[#3a6590] transition-colors">Create LOI</a>
              <span className="text-gray-300">|</span>
              <a href="/verify-loi" className="px-2 py-1 hover:text-[#3a6590] transition-colors">Verify LOI</a>
            </nav>

            {loggedInUser ? (
              <div className="flex items-center gap-3">
                <span className="hidden sm:flex items-center gap-1.5 text-sm text-gray-600">
                  <User className="w-4 h-4" />
                  {loggedInUser.first_name || loggedInUser.email}
                </span>
                <a
                  href="/admin/dashboard"
                  className="px-3 py-1.5 text-sm font-medium text-white rounded-lg"
                  style={{ backgroundColor: BRAND_BLUE }}
                >
                  Dashboard
                </a>
                {onLogout && (
                  <button
                    onClick={onLogout}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Logout
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowLoginModal(true)}
                  data-testid="nav-login-button"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition cursor-pointer"
                  style={{ backgroundColor: BRAND_BLUE }}
                >
                  <LogIn className="w-4 h-4" />
                  Login
                </button>
                <a
                  href="/register"
                  data-testid="nav-create-account-button"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition"
                  style={{ backgroundColor: BRAND_BLUE }}
                >
                  <User className="w-4 h-4" />
                  Sign up
                </a>
              </div>
            )}

            <LanguageSelector variant="public" showLabel={true} />
          </div>
        </div>
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 relative">
            <button
              onClick={() => { setShowLoginModal(false); setLoginError(''); }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="text-center mb-6">
              <div className="flex items-center justify-center mb-3">
                <img src={TRADOS_LOGO} alt="Trados" className="h-20" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">Welcome Back</h2>
              <p className="text-sm text-gray-500 mt-1">Login to access your dashboard</p>
            </div>

            {loginError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm mb-4">
                {loginError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={loginEmail}
                  onChange={e => setLoginEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyDown={e => e.key === 'Enter' && handleLogin()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={e => setLoginPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyDown={e => e.key === 'Enter' && handleLogin()}
                />
              </div>
              <button
                onClick={handleLogin}
                disabled={loggingIn}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-lg font-medium"
                style={{ backgroundColor: BRAND_BLUE }}
              >
                {loggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                {loggingIn ? 'Logging in...' : 'Login'}
              </button>
            </div>

            <div className="text-center mt-4 text-sm text-gray-500">
              Don't have an account?{' '}
              <a href="/register" className="text-[#2B4B6A] font-medium hover:underline">Sign up free</a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TradosPublicHeader;
